import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import { Camera, Video, Mic, MessageSquare, Loader2, Anchor, ArrowUpRight, Waves } from "lucide-react";

const TYPE_META = {
  screenshot: { icon: Camera, label: "Screenshot" },
  screen_recording: { icon: Video, label: "Recording" },
  voice: { icon: Mic, label: "Voice" },
  text: { icon: MessageSquare, label: "Note" },
};

const STATUS_COLOR = { new: "bg-danger", in_progress: "bg-sunny", resolved: "bg-grass" };

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function DashboardHome() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiClient.get("/stats").then(({ data }) => setStats(data)).catch(() => setStats({ total: 0 }));
  }, []);

  if (!stats) {
    return <div className="flex items-center justify-center h-screen"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>;
  }

  return (
    <div>
      <div className="h-20 flex items-center px-6 sm:px-8">
        <div>
          <h1 className="font-display text-3xl font-700 tracking-tight">Overview</h1>
          <p className="text-sm text-ink/60">Everything your testers hauled in.</p>
        </div>
      </div>
      <div className="p-6 lg:p-8 pt-2">
        {/* Bento stat grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <Link to="/projects" className="nb-card nb-card-hover bg-orange text-white p-6 lg:col-span-2 relative overflow-hidden block" data-testid="stat-total">
            <Waves className="absolute -right-4 -bottom-4 h-28 w-28 text-white/15" />
            <p className="text-xs font-800 uppercase tracking-wide mb-2 text-white/90">Total feedback</p>
            <p className="font-display text-6xl lg:text-7xl font-700 leading-none">{stats.total}</p>
            <p className="text-sm mt-3 text-white/90 font-600">across {stats.projects} project{stats.projects === 1 ? "" : "s"}</p>
          </Link>
          <StatCard label="New" value={stats.new} bg="bg-danger" text="text-white" testid="stat-new" to="/projects?status=new" />
          <StatCard label="In progress" value={stats.in_progress} bg="bg-sunny" text="text-ink" testid="stat-inprogress" to="/projects?status=in_progress" />
          <StatCard label="Resolved" value={stats.resolved} bg="bg-grass" text="text-white" testid="stat-resolved" to="/projects?status=resolved" />
          <div className="nb-card bg-teal p-6">
            <p className="text-xs font-800 uppercase tracking-wide mb-3">By type</p>
            <div className="space-y-2">
              {Object.entries(stats.by_type || {}).map(([k, v]) => {
                const M = TYPE_META[k];
                return (
                  <div key={k} className="flex items-center justify-between text-sm font-700">
                    <span className="flex items-center gap-2"><M.icon className="h-4 w-4" />{M.label}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent feedback */}
        <div className="nb-card p-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b-2 border-ink bg-sand">
            <h2 className="font-display font-700 text-xl">Recent catch</h2>
            <Link to="/projects" className="text-sm text-orange font-800 hover:underline flex items-center gap-1">
              View projects <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          {(!stats.recent || stats.recent.length === 0) ? (
            <div className="p-12 text-center">
              <Anchor className="h-12 w-12 text-ink/20 mx-auto mb-4" />
              <p className="text-ink/60 mb-5 font-600">No feedback yet. Launch a project and drop the widget into your beta.</p>
              <Link to="/projects" data-testid="empty-create-project" className="nb-btn bg-orange text-white px-5 py-2.5 text-sm">Create a project</Link>
            </div>
          ) : (
            <ul className="divide-y-2 divide-ink/10">
              {stats.recent.map((fb) => {
                const M = TYPE_META[fb.type] || TYPE_META.text;
                return (
                  <li key={fb.id}>
                    <Link to={`/projects/${fb.project_id}`} data-testid={`recent-row-${fb.id}`}
                      className="flex items-center gap-4 px-6 py-4 hover:bg-sand/60 transition-colors">
                      <span className={`h-3 w-3 rounded-full border-2 border-ink ${STATUS_COLOR[fb.status] || "bg-muted"}`} />
                      <span className="h-9 w-9 bg-cream border-2 border-ink rounded-xl flex items-center justify-center shrink-0"><M.icon className="h-4 w-4" /></span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate font-600">{fb.text_note || `${M.label} feedback`}</p>
                        {fb.project_name && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-800 uppercase px-2 py-0.5 rounded-full border-2 border-ink bg-teal text-ink" data-testid={`recent-project-${fb.id}`}>
                            <Anchor className="h-2.5 w-2.5" /> {fb.project_name}
                          </span>
                        )}
                      </div>
                      <span className="hidden sm:inline text-xs text-ink/60 font-700">{fb.submitter_name || "Anonymous"}</span>
                      <span className="text-xs text-ink/50 font-mono w-16 text-right shrink-0">{timeAgo(fb.created_at)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, bg, text, testid, to }) {
  return (
    <Link to={to} className={`nb-card nb-card-hover ${bg} ${text} p-6 block`} data-testid={testid}>
      <p className="text-xs font-800 uppercase tracking-wide mb-3">{label}</p>
      <p className="font-display text-5xl font-700 leading-none">{value}</p>
    </Link>
  );
}
